import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  Auth,
  UserCredential,
} from "firebase/auth";

// Auth-only config (no Firestore/Storage/etc. is used from this client) --
// projectId/authDomain are still required for signInWithCustomToken to hit
// the right Identity Toolkit project, not just the apiKey.
// A separate REACT_APP_firebaseApiKey, not REACT_APP_apiKey -- that name was already taken by
// receipt.jsx's Google Maps key (bootstrapURLKeys), an unrelated, differently-scoped API key.
// Reusing it here would have pointed the receipt page's map at a Firebase-restricted key that
// Maps JS API isn't authorized to use.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_firebaseApiKey!,
  authDomain: process.env.REACT_APP_authDomain!,
  projectId: process.env.REACT_APP_projectId!,
};

// getApps().length guard: Jest re-runs this module per test file, and
// initializeApp() throws on a second call for the same app in one runtime.
const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth: Auth = getAuth(app);

/**
 * Exchanges a Shared Login `customToken` (from `POST /v2/shared-login/account`)
 * for a real, persisted Firebase session. This is the step Shared Login's
 * provisioning was missing entirely (DASH-6345 prerequisite) -- without it,
 * `auth.currentUser` is always null and there is no ID token to authenticate
 * `GET/POST /v2/me/flight-alerts` with. The Firebase SDK itself keeps the
 * resulting session alive across reloads (IndexedDB-backed persistence);
 * nothing here needs to store the token.
 */
export const signInWithFirebaseCustomToken = (
  customToken: string,
): Promise<UserCredential> => signInWithCustomToken(auth, customToken);

/**
 * A fresh ID token for the currently signed-in Firebase user, or null if no one is signed in.
 * `getIdToken()` returns the cached token until it's within 5 minutes of expiry, then silently
 * refreshes -- callers never need their own refresh logic.
 *
 * Deliberately waits for `onAuthStateChanged`'s first callback rather than reading
 * `auth.currentUser` directly: on a fresh page load, the SDK restores a persisted session from
 * IndexedDB asynchronously, so `auth.currentUser` can still read `null` for a few milliseconds
 * after load even though a real session exists. Reading it synchronously here would race that
 * restore and misreport "not signed in" on every hard navigation to a page that calls this
 * immediately on mount (e.g. the Alert list). `onAuthStateChanged` always fires once with the
 * resolved state -- restored, freshly signed in, or genuinely signed out -- so waiting for it is
 * the correct fix, not a workaround.
 */
export const getCurrentIdToken = (): Promise<string | null> =>
  new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        if (!user) {
          resolve(null);
          return;
        }
        user.getIdToken().then(resolve, reject);
      },
      reject,
    );
  });

export const signOutOfFirebase = (): Promise<void> => firebaseSignOut(auth);
